import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface ApiRow {
  readonly apiId: string;
  readonly apiCode: string;
  readonly apiName: string;
  readonly apiType: string;
  readonly status: string;
  readonly currentVersion: string;
  readonly basePath: string;
  readonly authType: string;
}

interface ApiVersionRow {
  readonly versionId: string;
  readonly version: string;
  readonly changelog: string | null;
  readonly isCurrent: boolean;
}

const API_TYPES = ['', 'REST', 'SOAP', 'GRAPHQL', 'INTERNAL', 'EXTERNAL', 'PARTNER', 'MOBILE', 'SYSTEM'];
const STATUSES = ['', 'DRAFT', 'PUBLISHED', 'DEPRECATED', 'RETIRED'];
const CREATE_TYPES = ['REST', 'SOAP', 'GRAPHQL', 'INTERNAL', 'EXTERNAL', 'PARTNER', 'MOBILE', 'SYSTEM'];
const AUTH_TYPES = ['NONE', 'API_KEY', 'OAUTH2', 'JWT', 'BASIC', 'MTLS'];

/** M29 — API Catalog (§33 / §7-§9). Lists every registered API with its current
 * version, gateway type, auth scheme and lifecycle status, and operates the full
 * API governance lifecycle — register → publish → deprecate → retire, plus
 * versioning (new version, set current) and soft-delete — each wired to the
 * guarded /v1/eip/apis governance API (BR-03). */
export function M29ApiCatalogPage(): ReactNode {
  const { token } = useAuth();
  const { t: tr } = useTranslation();
  const [rows, setRows] = useState<ApiRow[]>([]);
  const [apiType, setApiType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // create form
  const [name, setName] = useState('');
  const [newType, setNewType] = useState('REST');
  const [basePath, setBasePath] = useState('');
  const [authType, setAuthType] = useState('API_KEY');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // version panel
  const [openVersionsFor, setOpenVersionsFor] = useState<string | null>(null);
  const [versions, setVersions] = useState<ApiVersionRow[]>([]);
  const [versionNo, setVersionNo] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (apiType) params.set('apiType', apiType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: ApiRow[] }>(`/v1/eip/apis${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, apiType, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m29.apiCatalog.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!name.trim()) { setActionError(tr('m29.apiCatalog.validationName')); return; }
    if (!basePath.trim()) { setActionError(tr('m29.apiCatalog.validationBasePath')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: ApiRow }>(`/v1/eip/apis`, {
        method: 'POST',
        body: { apiName: name.trim(), apiType: newType, basePath: basePath.trim(), authType },
        token,
      });
      setNotice(tr('m29.apiCatalog.noticeCreated', { code: r.data.apiCode }));
      setName('');
      setBasePath('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, tr('m29.apiCatalog.verbRegister'), 'M29_API_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const publish = (a: ApiRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/apis/${a.apiId}/publish`, { method: 'POST', body: {}, token }),
      tr('m29.apiCatalog.noticePublished', { code: a.apiCode }), tr('m29.apiCatalog.verbPublish'), 'M29_API_PUBLISH',
    );

  const deprecate = (a: ApiRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/apis/${a.apiId}/deprecate`, { method: 'POST', body: {}, token }),
      tr('m29.apiCatalog.noticeDeprecated', { code: a.apiCode }), tr('m29.apiCatalog.verbDeprecate'), 'M29_API_PUBLISH',
    );

  const retire = (a: ApiRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/apis/${a.apiId}/retire`, { method: 'POST', body: {}, token }),
      tr('m29.apiCatalog.noticeRetired', { code: a.apiCode }), tr('m29.apiCatalog.verbRetire'), 'M29_API_PUBLISH',
    );

  const remove = (a: ApiRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/apis/${a.apiId}`, { method: 'DELETE', token }),
      tr('m29.apiCatalog.noticeDeleted', { code: a.apiCode }), tr('m29.apiCatalog.verbDelete'), 'M29_API_MANAGE',
    );

  const loadVersions = async (a: ApiRow): Promise<void> => {
    if (openVersionsFor === a.apiId) { setOpenVersionsFor(null); return; }
    setActionError(null);
    setOpenVersionsFor(a.apiId);
    try {
      const r = await apiRequest<{ data: ApiVersionRow[] }>(`/v1/eip/apis/${a.apiId}/versions`, { token });
      setVersions(r.data);
    } catch (err) {
      setActionError(failMessage(err, tr('m29.apiCatalog.verbViewVersions'), 'M29_API_VIEW'));
      setVersions([]);
    }
  };

  const createVersion = async (a: ApiRow): Promise<void> => {
    if (!versionNo.trim()) { setActionError(tr('m29.apiCatalog.validationVersion')); return; }
    await runRowAction(
      () => apiRequest(`/v1/eip/apis/${a.apiId}/versions`, {
        method: 'POST', body: { version: versionNo.trim() }, token,
      }),
      tr('m29.apiCatalog.noticeVersionCreated', { code: a.apiCode }), tr('m29.apiCatalog.verbCreateVersion'), 'M29_API_MANAGE',
    );
    setVersionNo('');
    if (openVersionsFor === a.apiId) {
      try {
        const r = await apiRequest<{ data: ApiVersionRow[] }>(`/v1/eip/apis/${a.apiId}/versions`, { token });
        setVersions(r.data);
      } catch { /* surfaced by runRowAction */ }
    }
  };

  const setCurrent = (a: ApiRow, v: ApiVersionRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/eip/apis/${a.apiId}/versions/${v.versionId}/current`, { method: 'POST', body: {}, token }),
      tr('m29.apiCatalog.noticeSetCurrent', { version: v.version, code: a.apiCode }), tr('m29.apiCatalog.verbSetCurrent'), 'M29_API_MANAGE',
    );

  return (
    <main aria-label="api-catalog">
      <h1>{tr('m29.apiCatalog.title')}</h1>

      <section aria-label="create-api">
        <h2>{tr('m29.apiCatalog.registerHeading')}</h2>
        <form aria-label="api-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m29.apiCatalog.fieldName')}<input aria-label="field-api-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{tr('m29.apiCatalog.fieldType')}
            <select aria-label="field-api-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m29.apiCatalog.fieldBasePath')}<input aria-label="field-base-path" value={basePath} onChange={(e) => setBasePath(e.target.value)} /></label>
          <label>{tr('m29.apiCatalog.fieldAuth')}
            <select aria-label="field-auth-type" value={authType} onChange={(e) => setAuthType(e.target.value)}>
              {AUTH_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-api" disabled={busy}>{busy ? tr('m29.apiCatalog.registering') : tr('m29.apiCatalog.register')}</button>
        </form>
        {actionError && <p role="alert" aria-label="api-error">{actionError}</p>}
        {notice && <p aria-label="api-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m29.apiCatalog.filterApiType')}
          <select aria-label="filter-api-type" value={apiType} onChange={(e) => setApiType(e.target.value)}>
            {API_TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m29.apiCatalog.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m29.apiCatalog.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m29.apiCatalog.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m29.apiCatalog.loading')}</p> : (
        <>
          <p aria-label="api-count">{tr('m29.apiCatalog.count', { count: rows.length })}</p>
          <table aria-label="api-table">
            <thead><tr><th>{tr('m29.apiCatalog.colCode')}</th><th>{tr('m29.apiCatalog.colName')}</th><th>{tr('m29.apiCatalog.colType')}</th><th>{tr('m29.apiCatalog.colVersion')}</th><th>{tr('m29.apiCatalog.colAuth')}</th><th>{tr('m29.apiCatalog.colStatus')}</th><th>{tr('m29.apiCatalog.colActions')}</th></tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.apiId}>
                  <td>{a.apiCode}</td><td>{a.apiName}</td><td>{a.apiType}</td>
                  <td>{a.currentVersion}</td><td>{a.authType}</td><td>{a.status}</td>
                  <td>
                    <button type="button" aria-label={`publish-${a.apiCode}`} onClick={() => void publish(a)}>{tr('m29.apiCatalog.btnPublish')}</button>
                    <button type="button" aria-label={`deprecate-${a.apiCode}`} onClick={() => void deprecate(a)}>{tr('m29.apiCatalog.btnDeprecate')}</button>
                    <button type="button" aria-label={`retire-${a.apiCode}`} onClick={() => void retire(a)}>{tr('m29.apiCatalog.btnRetire')}</button>
                    <button type="button" aria-label={`versions-${a.apiCode}`} onClick={() => void loadVersions(a)}>{tr('m29.apiCatalog.btnVersions')}</button>
                    <button type="button" aria-label={`delete-${a.apiCode}`} onClick={() => void remove(a)}>{tr('m29.apiCatalog.btnDelete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {openVersionsFor && (() => {
            const api = rows.find((r) => r.apiId === openVersionsFor);
            if (!api) return null;
            return (
              <section aria-label="api-version-panel">
                <h2>{tr('m29.apiCatalog.versionsHeading', { code: api.apiCode })}</h2>
                <div>
                  <label>{tr('m29.apiCatalog.fieldVersion')}<input aria-label="field-version-no" value={versionNo} onChange={(e) => setVersionNo(e.target.value)} /></label>
                  <button type="button" aria-label="create-version" onClick={() => void createVersion(api)}>{tr('m29.apiCatalog.btnNewVersion')}</button>
                </div>
                <table aria-label="api-version-table">
                  <thead><tr><th>{tr('m29.apiCatalog.vColVersion')}</th><th>{tr('m29.apiCatalog.vColChangelog')}</th><th>{tr('m29.apiCatalog.vColCurrent')}</th><th>{tr('m29.apiCatalog.vColActions')}</th></tr></thead>
                  <tbody>
                    {versions.length === 0 ? (
                      <tr><td colSpan={4}>{tr('m29.apiCatalog.noVersions')}</td></tr>
                    ) : versions.map((v) => (
                      <tr key={v.versionId}>
                        <td>{v.version}</td><td>{v.changelog ?? '—'}</td><td>{v.isCurrent ? tr('m29.apiCatalog.current') : '—'}</td>
                        <td>
                          <button type="button" aria-label={`set-current-${v.version}`} onClick={() => void setCurrent(api, v)}>{tr('m29.apiCatalog.btnSetCurrent')}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            );
          })()}
        </>
      )}
    </main>
  );
}
